import { useEffect, useMemo, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { DataTableFilterMeta } from 'primereact/datatable';
import { getUserId } from '@/core/utils/tokenUtils';
import {
    decodeTableViewStateParams,
    decodeTableViewState,
    encodeTableViewState,
    encodeTableViewStateParams,
    hasTableViewStateParams,
    type TableViewState,
} from '@/shared/lib/tableViewState';

/** Читает сохранённые настройки таблицы */
const readStoredState = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

/** Запоминает настройки таблицы */
const writeStoredState = (key: string, value: string | null) => {
    try {
        if (value) window.localStorage.setItem(key, value);
        else window.localStorage.removeItem(key);
    } catch {
        return;
    }
};

/** Сохраняет состояние таблицы в URL и восстанавливает его при возврате в список */
export const usePersistentTableState = (defaults: TableViewState) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [, refresh] = useReducer((value: number) => value + 1, 0);
    const storageKey = `vox.table.v1:${getUserId() ?? 'anonymous'}:${location.pathname}`;
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const hasUrlState = hasTableViewStateParams(params, defaults);
    const legacyState = params.get('table');
    const storedState = readStoredState(storageKey);

    const state = useMemo(() => {
        if (legacyState !== null) return decodeTableViewState(legacyState, defaults);
        if (hasUrlState) return decodeTableViewStateParams(params, defaults);
        return decodeTableViewState(storedState, defaults);
    }, [defaults, hasUrlState, legacyState, params, storedState]);

    useEffect(() => {
        const encoded = encodeTableViewState(state, defaults);
        const nextParams = new URLSearchParams(location.search);
        encodeTableViewStateParams(nextParams, state, defaults);

        if (hasUrlState) {
            writeStoredState(storageKey, encoded);
        } else if (storedState !== null && !encoded) {
            writeStoredState(storageKey, null);
        }

        if (nextParams.toString() !== location.search.replace(/^\?/, '')) {
            void navigate({
                pathname: location.pathname,
                search: nextParams.toString()
            }, {
                replace: true,
                state: location.state,
            });
        }
    }, [defaults, hasUrlState, location.pathname, location.search, location.state, navigate, state, storageKey, storedState]);

    /** Применяет и сохраняет настройки таблицы */
    const commit = (nextState: TableViewState) => {
        const encoded = encodeTableViewState(nextState, defaults);
        writeStoredState(storageKey, encoded);

        const nextParams = new URLSearchParams(location.search);
        encodeTableViewStateParams(nextParams, nextState, defaults);

        const search = nextParams.toString();

        if (search !== location.search.replace(/^\?/, '')) {
            void navigate({
                pathname: location.pathname,
                search
            }, {
                replace: true,
                state: location.state,
            });
        } else {
            refresh();
        }
    };

    /** Меняет условия поиска таблицы */
    const setFilters = (filters: DataTableFilterMeta) => commit({
        ...state,
        filters,
        first: 0,
    });

    /** Меняет текст общего поиска таблицы */
    const setSearch = (search: string) => commit({
        ...state,
        search,
        first: 0,
        filters: 'global' in state.filters && 'value' in state.filters.global
            ? {
                ...state.filters,
                global: {
                    ...state.filters.global,
                    value: search || null
                }
            }
            : state.filters,
    });

    /** Меняет страницу таблицы */
    const setPage = (first: number, rows: number) => commit({
        ...state,
        first,
        rows
    });

    /** Меняет порядок строк таблицы */
    const setSort = (sortField: string, sortOrder: number) => commit({
        ...state,
        first: 0,
        sortField,
        sortOrder,
    });

    /** Возвращает исходные настройки таблицы */
    const reset = () => commit(defaults);

    return {
        ...state,
        setFilters,
        setSearch,
        setPage,
        setSort,
        reset
    };
};
